import React, { useState, useEffect } from "react";
import { Box, Tabs, Tab, Typography, CircularProgress } from "@mui/material";
import ChangePasswordForm from "../patient/ChangePasswordForm";
import EmailPreferences from "../components/EmailPreferences";
import { SERVER_BASE_URL } from "../Config";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`pharmacy-profile-tabpanel-${index}`}
      aria-labelledby={`pharmacy-profile-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface PharmacyProfileProps {
  onPasswordChanged?: () => void;
}

const PharmacyProfile: React.FC<PharmacyProfileProps> = ({ onPasswordChanged }) => {
  const [tabValue, setTabValue] = useState(0);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [permissionLevel, setPermissionLevel] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const token = localStorage.getItem("pharmacistToken");
        const response = await fetch(`${SERVER_BASE_URL}/pharmacist/profile`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();
        if (data.success) {
          setName(data.pharmacist.name || "");
          setEmail(data.pharmacist.email || "");
          setPermissionLevel(data.pharmacist.permissionLevel || "");
        }
      } catch (err) {
        console.error("Failed to load profile:", err);
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: 600, mx: "auto", mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        Profile
      </Typography>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} aria-label="pharmacy profile tabs">
          <Tab label="Account" id="pharmacy-profile-tab-0" aria-controls="pharmacy-profile-tabpanel-0" />
          <Tab label="Security" id="pharmacy-profile-tab-1" aria-controls="pharmacy-profile-tabpanel-1" />
          <Tab label="Notifications" id="pharmacy-profile-tab-2" aria-controls="pharmacy-profile-tabpanel-2" />
        </Tabs>
      </Box>
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ textAlign: "left" }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>Name:</strong> {name}
          </Typography>
          <Typography variant="body1">
            <strong>Email:</strong> {email}
          </Typography>
        </Box>
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <ChangePasswordForm tokenKey="pharmacistToken" onSuccess={onPasswordChanged} />
      </TabPanel>
      <TabPanel value={tabValue} index={2}>
        <EmailPreferences
          tokenKey="pharmacistToken"
          defaultOn={false}
          emailTypes={[
            { key: "new_order", label: "New prescription order placed" },
            { key: "cancellation_request", label: "Cancellation requested" },
            { key: "provider_support", label: "Provider support request" },
            { key: "provider_request", label: "New provider account request" },
          ]}
        />
      </TabPanel>
    </Box>
  );
};

export default PharmacyProfile;
